import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<AgentAuthorityConsentView />` — Mode C consent flow at Smart Health
 * Account for AI agent authority delegation per CPL Mode C consent flow
 * v0.1 (`CSO/Product/cpl-mode-c-agent-authority-delegation-consent-flow-
 * v0.1.md`, 2026-05-16).
 *
 * Composes Stages (2)–(4) of the canonical 5-stage flow:
 *
 *   - Stage (2) Patient arrival at PHG origin — page chrome + Authority
 *     Indicator + substrate-honesty disclaimer
 *   - Stage (3) Authority review modal — 560px width per DD v0.1 §5;
 *     scope + duration + revocation framing + Grant/Decline CTAs
 *   - Stage (4) Grant/Decline decision substrate-routing — sub-state
 *     machine `idle → routing → granted | declined`; on grant, Authority
 *     Indicator transitions `inactive → sandbox → active`; on decline,
 *     confirmation modal then bounceback
 *
 * Component 1 Consent UI Seam wrapper discipline canonical preservation
 * forward — this view is the patient-side surface that AI agent clients
 * bounce TO via OAuth-style handback URL; partner apps NEVER render
 * PHG consent UI.
 *
 * # Doctrinal anchors preserved
 *
 *   - Component 1 Consent UI Seam wrapper — PHG-origin rendering;
 *     never rendered inside a partner-app surface
 *   - Patient-fiduciary doctrine (#4) — every authority decision
 *     surfaces at the patient's own surface; never silent
 *   - NO-SHT-BACKDOOR (#5) — handle issuance is substrate-routed via
 *     DEV6 sandbox-auth-backend; no SHT key material flows through
 *     this surface
 *   - Hub payload-blindness (#6) — explicit framing at every CTA +
 *     modal copy: "Smart Health Network routes the authority; your
 *     records do not move"
 *   - Substrate-as-router not substrate-as-decision-maker — required
 *     substrate-honesty disclaimer at every stage
 *   - Vendor-naming-linguistic-not-branded-patches — agent name
 *     typographically rendered; never logo-rendered
 *
 * # Sandbox-grade scope-fence
 *
 * v0 renders Stages (2)–(4) UX with a `IAgentHandleIssuanceClient`
 * abstraction. Stage (4) substrate routing uses a mock client by
 * default; production mounts inject the real client wrapping DEV6's
 * sandbox-auth-backend `/v0/agent-handle/issue` endpoint when that
 * endpoint ships at the production tier.
 */
import * as React from 'react';
import { useLocale } from '../i18n/locale-context.js';
import { AuthorityIndicator, } from '../widgets/authority-indicator.js';
const STATE_TO_AUTHORITY = {
    idle: 'inactive',
    review: 'inactive',
    routing: 'sandbox',
    granted: 'active',
    'decline-confirm': 'inactive',
    declined: 'inactive',
};
const STATE_TO_LABEL_KEY = {
    idle: 'account.consent.agent.status.not_yet_granted',
    review: 'account.consent.agent.status.not_yet_granted',
    routing: 'account.consent.agent.status.substrate_routing',
    granted: 'account.consent.agent.status.granted',
    'decline-confirm': 'account.consent.agent.status.not_yet_granted',
    declined: 'account.consent.agent.status.declined',
};
/**
 * Deterministic mock for dev + tests. Resolves with a synthetic handle
 * after a brief simulated substrate-routing latency.
 */
export class MockAgentHandleIssuanceClient {
    latencyMs;
    constructor(opts) {
        this.latencyMs = opts?.latencyMs ?? 800;
    }
    issueHandle = async (req) => {
        if (this.latencyMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, this.latencyMs));
        }
        return {
            handleId: `handle_${req.agentId}_${req.requestId}`,
            issuedAt: new Date().toISOString(),
        };
    };
}
export function AgentAuthorityConsentView(props) {
    const { t } = useLocale();
    const [flowState, setFlowState] = React.useState('idle');
    const [issuanceError, setIssuanceError] = React.useState(undefined);
    const agentName = props.agentName ?? props.agentId;
    const handleGrant = React.useCallback(() => {
        setFlowState('routing');
        setIssuanceError(undefined);
        void (async () => {
            try {
                const result = await props.handleIssuanceClient.issueHandle({
                    requestId: props.requestId,
                    agentId: props.agentId,
                    scopeSummary: props.scopeSummary,
                });
                setFlowState('granted');
                props.onGranted?.(result);
            }
            catch (err) {
                const message = err instanceof Error ? err.message : 'Unknown error';
                setIssuanceError(message);
                setFlowState('review');
            }
        })();
    }, [props]);
    const handleDeclineConfirm = React.useCallback(() => {
        setFlowState('declined');
        props.onDeclined?.();
    }, [props]);
    return (_jsxs("section", { "aria-labelledby": "agent-consent-heading", className: "agent-consent-view", "data-testid": "agent-consent-view", "data-flow-state": flowState, children: [_jsxs("header", { className: "agent-consent-view__header", children: [_jsx("h2", { id: "agent-consent-heading", children: t('account.consent.agent.heading') }), _jsx("p", { className: "agent-consent-view__subhead", children: t('account.consent.agent.subhead', { agentName }) })] }), _jsx(AuthorityIndicator, { state: STATE_TO_AUTHORITY[flowState], contextLabel: flowState === 'declined'
                    ? t('account.consent.agent.status.declined')
                    : t(STATE_TO_LABEL_KEY[flowState], { agentName }) }), _jsx("p", { className: "agent-consent-view__substrate-honesty", "data-testid": "agent-consent-substrate-honesty", children: t('account.consent.agent.substrate_role') }), flowState === 'idle' ? (_jsx("button", { type: "button", className: "agent-consent-view__open-review-cta", onClick: () => setFlowState('review'), "data-testid": "agent-consent-open-review-cta", children: t('account.consent.agent.open_review_cta') })) : null, flowState === 'review' || flowState === 'routing' ? (_jsx(AuthorityReviewModal, { agentName: agentName, scopeSummary: props.scopeSummary, scopeSubDetail: props.scopeSubDetail, durationLabel: props.durationLabel, isRouting: flowState === 'routing', issuanceError: issuanceError, onGrant: handleGrant, onDecline: () => setFlowState('decline-confirm'), onClose: flowState === 'routing'
                    ? () => {
                        /* close disabled while routing */
                    }
                    : () => setFlowState('idle') })) : null, flowState === 'decline-confirm' ? (_jsx(DeclineConfirmModal, { agentName: agentName, onConfirm: handleDeclineConfirm, onCancel: () => setFlowState('review') })) : null, flowState === 'granted' ? (_jsxs("div", { role: "status", className: "agent-consent-view__granted-state", "data-testid": "agent-consent-granted", children: [_jsx("p", { children: t('account.consent.agent.granted_body', { agentName }) }), props.returnUrl !== undefined ? (_jsx("a", { href: props.returnUrl, className: "agent-consent-view__bounceback-link", "data-testid": "agent-consent-bounceback-link", children: t('account.consent.agent.bounceback_link', { agentName }) })) : null] })) : null, flowState === 'declined' ? (_jsxs("div", { role: "status", className: "agent-consent-view__declined-state", "data-testid": "agent-consent-declined", children: [_jsx("p", { children: t('account.consent.agent.declined_body', { agentName }) }), props.returnUrl !== undefined ? (_jsx("a", { href: props.returnUrl, className: "agent-consent-view__bounceback-link", "data-testid": "agent-consent-decline-bounceback-link", children: t('account.consent.agent.decline_bounceback_link') })) : null] })) : null] }));
}
function AuthorityReviewModal(props) {
    const { t } = useLocale();
    React.useEffect(() => {
        const handler = (ev) => {
            if (ev.key === 'Escape' && !props.isRouting)
                props.onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [props]);
    return (_jsx("div", { className: "agent-consent-review__backdrop", "data-testid": "agent-consent-review-modal", role: "presentation", onClick: props.isRouting ? undefined : props.onClose, children: _jsxs("div", { className: "agent-consent-review__dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": "agent-consent-review-heading", onClick: (ev) => ev.stopPropagation(), children: [_jsxs("header", { className: "agent-consent-review__header", children: [_jsx("h3", { id: "agent-consent-review-heading", children: t('account.consent.agent.review.heading') }), _jsx("p", { className: "agent-consent-review__agent-name", children: props.agentName })] }), _jsxs("dl", { className: "agent-consent-review__detail", children: [_jsxs("div", { className: "agent-consent-review__detail-row", children: [_jsx("dt", { children: t('account.consent.agent.scope_label') }), _jsx("dd", { children: props.scopeSummary })] }), props.scopeSubDetail !== undefined ? (_jsx("div", { className: "agent-consent-review__detail-row agent-consent-review__detail-row--sub", children: _jsx("dd", { className: "agent-consent-review__sub-detail", children: props.scopeSubDetail }) })) : null, _jsxs("div", { className: "agent-consent-review__detail-row", children: [_jsx("dt", { children: t('account.consent.agent.duration_label') }), _jsx("dd", { children: props.durationLabel })] })] }), _jsx("p", { className: "agent-consent-review__revocation-framing", children: t('account.consent.agent.revocation_framing') }), props.issuanceError !== undefined ? (_jsx("div", { role: "alert", className: "agent-consent-review__error", "data-testid": "agent-consent-review-error", children: t('account.consent.agent.issuance_error', {
                        detail: props.issuanceError,
                    }) })) : null, _jsxs("div", { className: "agent-consent-review__cta-group", children: [_jsx("button", { type: "button", className: "agent-consent-review__decline-cta", onClick: props.onDecline, disabled: props.isRouting, "data-testid": "agent-consent-decline-cta", children: t('account.consent.agent.decline_cta') }), _jsx("button", { type: "button", className: "agent-consent-review__grant-cta", onClick: props.onGrant, disabled: props.isRouting, "data-testid": "agent-consent-grant-cta", children: props.isRouting
                                ? t('account.consent.agent.routing_cta')
                                : t('account.consent.agent.grant_cta') })] })] }) }));
}
function DeclineConfirmModal(props) {
    const { t } = useLocale();
    React.useEffect(() => {
        const handler = (ev) => {
            if (ev.key === 'Escape')
                props.onCancel();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [props]);
    return (_jsx("div", { className: "agent-consent-decline__backdrop", "data-testid": "agent-consent-decline-confirm-modal", role: "presentation", onClick: props.onCancel, children: _jsxs("div", { className: "agent-consent-decline__dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": "agent-consent-decline-heading", onClick: (ev) => ev.stopPropagation(), children: [_jsx("h3", { id: "agent-consent-decline-heading", children: t('account.consent.agent.decline_confirm_heading') }), _jsx("p", { children: t('account.consent.agent.decline_confirm_body', {
                        agentName: props.agentName,
                    }) }), _jsxs("div", { className: "agent-consent-decline__cta-group", children: [_jsx("button", { type: "button", className: "agent-consent-decline__cancel-cta", onClick: props.onCancel, "data-testid": "agent-consent-decline-cancel-cta", children: t('account.consent.agent.decline_cancel_cta') }), _jsx("button", { type: "button", className: "agent-consent-decline__confirm-cta", onClick: props.onConfirm, "data-testid": "agent-consent-decline-confirm-cta", children: t('account.consent.agent.decline_confirm_cta') })] })] }) }));
}
//# sourceMappingURL=agent-authority-consent-view.js.map